import {
    EntityRef,
    IPage,
    EntityType,
    Identity,
    Page,
    EntityFactory,
    validUrlRegex,
    Team
  } from "@amzn/ask-legal-domain";
import { UIModel } from "./ui-model";
import * as React from "react";
import { Builder } from "builder-pattern";

/** Compare two arrays by a stable identifier — avoids JSON.stringify fragility */
function arraysEqualById<T>(a: T[], b: T[], getId: (item: T) => string): boolean {
    if (a.length !== b.length) return false;
    const aIds = a.map(getId).sort();
    const bIds = b.map(getId).sort();
    return aIds.every((id, i) => id === bIds[i]);
}

export namespace PageModel {
    export const NAME_CHAR_LIMIT = 50;
    export const TITLE_CHAR_LIMIT = 100;
    export const DESCRIPTION_CHAR_LIMIT = 500;

    export class CreateState {
        name: UIModel.State<string>;
        title: UIModel.State<string>;
        description: UIModel.State<string>;
        pageOwner: UIModel.State<Identity>;
        secondaryPageOwners: UIModel.State<Identity[]>;
        secondaryPageOwnerTeams: UIModel.State<Team[]>;
        pageLibraryRef: EntityRef;
        errorText: string;
        reset: () => void;

        static toInput (state: CreateState): IPage.CreatePageInput {
            return IPage.CreatePageInput.create({
                name: state.name.value,
                title: state.title.value,
                description: state.description.value,
                pageOwner: state.pageOwner.value,
                secondaryPageOwners: state.secondaryPageOwners.value?.length > 0 ? state.secondaryPageOwners.value : undefined,
                secondaryPageOwnerTeams: state.secondaryPageOwnerTeams.value?.length > 0 ? state.secondaryPageOwnerTeams.value : undefined,
                pageLibraryId: state.pageLibraryRef.id
            });
        }

        static use (props: {
            template?: IPage.CreatePageInput
            pageLibraryid: string
        }): CreateState {
            const name = UIModel.State.useWithRegexAndCharLimit({
                regex: new RegExp(/^[a-zA-Z0-9]+$/),
                characterLimit: NAME_CHAR_LIMIT
            });
            const title = UIModel.State.useNotNullStringWithCharLimit({ characterLimit: TITLE_CHAR_LIMIT });
            const description = UIModel.State.useNotNullStringWithCharLimit({ characterLimit: DESCRIPTION_CHAR_LIMIT });
            const pageOwner = UIModel.State.useRequired<Identity>({});
            const secondaryPageOwners = UIModel.State.use<Identity[]>({ initialValue: [] });
            const secondaryPageOwnerTeams = UIModel.State.use<Team[]>({ initialValue: [] });

            const reset = () => {
                name.setValue(null);
                title.setValue(null);
                description.setValue(null);
                pageOwner.setValue(null);
                secondaryPageOwners.setValue([]);
                secondaryPageOwnerTeams.setValue([]);
            };

            React.useEffect(() => {
                if (!props.template) return;
                name.setValue(props.template.name);
                title.setValue(props.template.title);
                description.setValue(props.template.description);
                pageOwner.setValue(props.template.pageOwner);
                secondaryPageOwners.setValue(props.template.secondaryPageOwners || []);
                secondaryPageOwnerTeams.setValue(props.template.secondaryPageOwnerTeams || []);
            }, []);

            return Builder<CreateState>(new CreateState())
                .name(name)
                .title(title)
                .description(description)
                .pageOwner(pageOwner)
                .secondaryPageOwners(secondaryPageOwners)
                .secondaryPageOwnerTeams(secondaryPageOwnerTeams)
                .pageLibraryRef(Builder<EntityRef>().id(props.pageLibraryid).type(EntityType.PageLibrary).version(1).build())
                .reset(reset)
                .build();
        }
    }

    export class UpdateMetadataState {
        targetRef: EntityRef;
        pageOwner: UIModel.State<Identity>;
        secondaryPageOwners: UIModel.State<Identity[]>;
        secondaryPageOwnerTeams: UIModel.State<Team[]>;
        forwardingUrl: UIModel.State<string>;
        errorText: string;
        reset: () => void;
        isDirty: () => boolean;

        static toInput (state: UpdateMetadataState): IPage.UpdatePageMetadataInput {
            return IPage.UpdatePageMetadataInput.create({
                newPageOwner: state.pageOwner.value,
                // Send [] to clear secondary owners; undefined means "no change"
                newSecondaryPageOwners: state.secondaryPageOwners.value ?? undefined,
                newSecondaryPageOwnerTeams: state.secondaryPageOwnerTeams.value ?? undefined,
                forwardingUrl: state.forwardingUrl.value?.length === 0 ? undefined : state.forwardingUrl.value,
                targetRef: state.targetRef
            });
        }

        static use (props: {
            template: Page.Data
        }): UpdateMetadataState {
            const newPageOwner = UIModel.State.useRequired<Identity>({});
            const secondaryPageOwners = UIModel.State.use<Identity[]>({ initialValue: [] });
            const secondaryPageOwnerTeams = UIModel.State.use<Team[]>({ initialValue: [] });
            const forwardingUrl = UIModel.State.useOptionalWithRegexValidation({ regex: validUrlRegex });

            const reset = () => {
                newPageOwner.setValue(props.template.pageOwner);
                secondaryPageOwners.setValue(props.template.secondaryPageOwners || []);
                secondaryPageOwnerTeams.setValue(props.template.secondaryPageOwnerTeams || []);
                forwardingUrl.setValue(props.template.forwardingUrl);
            };

            const isDirty = () => {
                if (
                    newPageOwner.value === props.template.pageOwner &&
                    arraysEqualById(secondaryPageOwners.value, props.template.secondaryPageOwners || [], o => o.id) &&
                    arraysEqualById(secondaryPageOwnerTeams.value, props.template.secondaryPageOwnerTeams || [], t => t.teamId) &&
                    forwardingUrl.value === props.template.forwardingUrl
                ) return false;
                return true;
            };

            React.useEffect(() => {
                newPageOwner.setValue(props.template.pageOwner);
                secondaryPageOwners.setValue(props.template.secondaryPageOwners || []);
                secondaryPageOwnerTeams.setValue(props.template.secondaryPageOwnerTeams || []);
                forwardingUrl.setValue(props.template.forwardingUrl);
            }, []);

            return Builder<UpdateMetadataState>(new UpdateMetadataState())
                .targetRef(EntityFactory.toEntityRef(props.template))
                .pageOwner(newPageOwner)
                .secondaryPageOwners(secondaryPageOwners)
                .secondaryPageOwnerTeams(secondaryPageOwnerTeams)
                .forwardingUrl(forwardingUrl)
                .reset(reset)
                .isDirty(isDirty)
                .build();
        }
    }

    export class DeprecatePageState {
        targetRef: EntityRef;
        forwardingUrl: UIModel.State<string>;
        reset: () => void;
        isDirty: () => boolean;

        static toInput (state: DeprecatePageState): IPage.DeprecatePageInput {
            return IPage.DeprecatePageInput.create(
                state.targetRef.id,
                state.forwardingUrl.value?.length === 0 ? undefined : state.forwardingUrl.value
            );
        }

        static use (props: {
            entityRef: EntityRef;
            forwardingUrl?: string;
        }): DeprecatePageState {
            const forwardingUrl = UIModel.State.useOptionalWithRegexValidation({ regex: validUrlRegex });

            const reset = () => {
                forwardingUrl.setValue(props.forwardingUrl);
            };

            const isDirty = () => {
                if (forwardingUrl.value === props.forwardingUrl) return false;
                return true;
            };

            React.useEffect(() => {
                if (!props.entityRef) return;
                forwardingUrl.setValue(props.forwardingUrl);
            }, []);

            return Builder<DeprecatePageState>(new DeprecatePageState())
                .targetRef(props.entityRef)
                .forwardingUrl(forwardingUrl)
                .reset(reset)
                .isDirty(isDirty)
                .build();
        }
    }
}